<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import AuthBrandPanel from '@/components/domain/AuthBrandPanel.vue'
import LanguagePicker from '@/components/domain/LanguagePicker.vue'
import ThemePicker from '@/components/domain/ThemePicker.vue'

/**
 * Auth-Routes (Login, Forgot-Password, Reset-Password) teilen sich ein
 * persistentes 2-Spalten-Layout: links das Brand-Panel, rechts die
 * jeweilige Form. So mountet sich das Brand-Panel beim Wechsel zwischen
 * Login und Forgot-Password NICHT neu — der `brandIn`-Reveal und der
 * Dial-Spin starten nicht jedes Mal bei 0, der Uebergang wirkt smooth.
 *
 * Nicht-Auth-Routes (= eingeloggte App) rendern weiterhin nur den
 * RouterView (AppShell uebernimmt sein eigenes Sidebar-Layout).
 */
const route = useRoute()

// Pragmatisch: alle „public"-Routen sind Auth-Seiten. Falls das mal
// nicht mehr stimmt (z.B. Help-Seite), gibt es eine eigene Route-Meta.
const isAuth = computed(() => route.meta.public === true)

// Body-Scroll-Lock auf Auth-Seiten — sonst kann auf kleinen Screens
// die Form-Section unter dem Brand-Panel verschwinden. Stoffwechsel
// passiert vor allem im Login → Forgot-Wechsel.
function applyScrollLock(lock: boolean) {
  if (lock) document.body.classList.add('auth-no-scroll')
  else document.body.classList.remove('auth-no-scroll')
}
watch(isAuth, applyScrollLock, { immediate: true })
onMounted(() => applyScrollLock(isAuth.value))
onUnmounted(() => applyScrollLock(false))
</script>

<template>
  <div v-if="isAuth" class="auth-shell">
    <AuthBrandPanel />
    <section class="auth-shell-form">
      <div class="auth-shell-controls">
        <LanguagePicker />
        <ThemePicker />
      </div>
      <div class="auth-shell-formwrap">
        <!--
          Bewusst KEIN `<Transition mode="out-in">` um den RouterView:
          mit lazy-geladenen Route-Komponenten (siehe router/index.ts)
          haengt der Wechsel mid-flight — die out-Phase blendet die alte
          Komponente aus, danach steht nichts da, weil die neue async
          noch nicht da ist. Der eigentliche Smoothness-Gewinn kommt
          ohnehin aus dem persistenten Brand-Panel + der `formIn`-CSS-
          Animation (in `_auth-shared.css`), die jede neue Form-Pane
          sanft einblendet.
        -->
        <RouterView />
      </div>
    </section>
  </div>
  <RouterView v-else />
</template>

<style>
/*
 * Shared Auth-Layout-Styles importieren wir hier — die Klassen
 * `.auth-shell`, `.auth-shell-form`, `.auth-shell-formwrap`,
 * `.auth-shell-controls` und das `<AuthBrandPanel>` brauchen sie auf
 * JEDER Auth-Route, also auch wenn die LoginView ohne eigenes
 * `@import` aktiv ist.
 */
@import '@/views/_auth-shared.css';

/* Auth-Layout-Scroll-Lock — siehe Kommentar im Script. */
body.auth-no-scroll {
  overflow: hidden;
}
</style>
